import * as React from 'react'

import {useFormik} from 'formik'
import invariant from 'invariant'

import * as priceApi from '../../services/api/price'
import {
  formDataToPartialPriceRequest,
  getFormulaLabel,
  pricingsToFormData,
} from '../forms/pricings/utils'
import {isMixedValue, toMixedValidationSchema, toTouchedFormData} from '../utils/form'

import {Price, PriceFormula, Provider} from '../../types'

import Grid from '../../../../commons/src/components/grid'
import GridCell from '../../../../commons/src/components/grid/GridCell'
import Headline from '../../../../commons/src/components/headline'
import Paragraph from '../../../../commons/src/components/paragraph'
import Stack from '../../../../commons/src/components/stack/Stack'

import PricingsForm, {
  MixedFormData,
  SingleFormData,
  getInitialValues,
  validationSchema,
} from '../forms/pricings/form'
import SaveButtonPartial from '../save-button-partial'

import PriceCalculatorPartial from './price-calculator-partial'

type Props = {
  pricings: Price[]
  provider: Provider | null
  hidePriceCalculator?: boolean
  onDone?: () => void
}

const EditPricingFormPartial = ({
  pricings,
  provider,
  hidePriceCalculator = false,
  onDone,
}: Props) => {
  invariant(provider, 'Provider is missing.')

  const formik = useFormik<MixedFormData>({
    initialValues: {
      ...getInitialValues(PriceFormula.THREE_D_PRINTING),
      ...pricingsToFormData(pricings),
    },
    validationSchema: toMixedValidationSchema(validationSchema),
    enableReinitialize: true,
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(
        values,
        formik.initialValues
      ) as Partial<SingleFormData>
      const priceRequest = formDataToPartialPriceRequest(touchedValues)

      try {
        const pricingIds = pricings.map(({id}) => id)
        await priceApi.patchMultiple(priceRequest, pricingIds)
        onDone?.()
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  const isSomeValueForCalculatorMixed = [
    formik.values.pricingType,
    formik.values.weightPrice,
    formik.values.density,
    formik.values.volumePrice,
    formik.values.boundingBoxPrice,
    formik.values.surfacePrice,
    formik.values.baseUnitPrice,
    formik.values.minimumUnitPrice,
    formik.values.quantityDiscount,
    formik.values.quantityDiscountCap,
    formik.values.basePrice,
    formik.values.minimumPrice,
    formik.values.machiningCostPerHour,
    formik.values.machiningMaterialRemovalRate,
    formik.values.finishingMaterialRemovalRate,
  ].some(isMixedValue)

  if (isMixedValue(formik.values.formula)) {
    // This is a fallback: Selected pricings will not be editable if their formulas are mixed.
    return (
      <Paragraph warning className="u-no-margin-bottom">
        {`The selected pricings have different formulas. It's not possible to edit pricings with different formulas.`}
      </Paragraph>
    )
  }

  if (hidePriceCalculator) {
    return (
      <Stack space="300">
        <PricingsForm formik={formik} provider={provider} />
        <SaveButtonPartial formik={formik} />
      </Stack>
    )
  }

  return (
    <Grid>
      <GridCell l={6}>
        <Stack space="300">
          <Stack space="70">
            <Headline size="s" label="Formula" className="u-no-margin-bottom" />
            <Paragraph className="u-no-margin-bottom">
              {getFormulaLabel(formik.values.formula)}
            </Paragraph>
          </Stack>
          <PricingsForm formik={formik} provider={provider} />
          <SaveButtonPartial formik={formik} />
        </Stack>
      </GridCell>
      <GridCell s={0} l={6}>
        <PriceCalculatorPartial
          priceRequest={
            isSomeValueForCalculatorMixed
              ? undefined
              : formDataToPartialPriceRequest(formik.values as Partial<SingleFormData>)
          }
          provider={provider}
          pricingType={
            isMixedValue(formik.values.pricingType) ? undefined : formik.values.pricingType
          }
        />
      </GridCell>
    </Grid>
  )
}

export default EditPricingFormPartial
